/**
 * Numerical first derivative approximation using central difference method
 * @param {function} func Function to differentiate
 * @param {number} x Point to evaluate derivative
 * @param {number} h Step size
 * @returns {number} Approximated derivative
 */
function centralDifferenceDfDx(func, x, h) {
  return (func(x + h) - func(x - h)) / (2 * h);
}

/**
 * Numerical second derivative approximation using central difference method
 * @param {function} func Function to differentiate
 * @param {number} x Point to evaluate derivative
 * @param {number} h Step size
 * @returns {number} Approximated derivative
 */
function centralDifferenceDfDx2(func, x, h) {
  return (func(x + h) - 2 * func(x) + func(x - h)) / h ** 2;
}

/**
 * Richardson extrapolation relation
 * @param {number} fullStepApprox Value at full step size
 * @param {number} halfStepApprox Value at half step size
 * @param {number} k Extrapolation order
 * @returns {number} Extrapolated value
 */
function richardsonExtrapolate(fullStepApprox, halfStepApprox, k) {
  return (4 ** k * halfStepApprox - fullStepApprox) / (4 ** k - 1);
}

/**
 * Richardson error extrapolation relation
 * @param {number} fullStepApprox Value at full step size
 * @param {number} halfStepApprox Value at half step size
 * @param {number} k Extrapolation order
 * @returns {number} Extrapolated error
 */
function richardsonErrorExtrapolate(fullStepApprox, halfStepApprox, k) {
  return (4 ** (k + 1) / (4 ** (k + 1) - 1)) * (fullStepApprox - halfStepApprox);
}

/**
 * Nested Richardson extrapolation method for adaptive step size derivative estimation
 * @param {function} f Function to differentiate
 * @param {number} x Point to evaluate derivative
 * @param {number} h0 Initial step size (ideally larger but near optimal step size)
 * @param {number} errorTol Tolerance for estimated error
 * @param {number} maxK Maximum order of extrapolation
 * @param {number} order Order of derivative to estimate (1 or 2)
 * @param {boolean} fullOutput Return full output including error and estimate table
 * @returns {number|object} Estimated derivative or full output object
 */
export function AdaptiveRichardsonDeriv(f, x, h0, errorTol = 1e-8, maxK = 4, order = 1, fullOutput = true) {
  const checkErrorRequirement = (errors) => Math.abs(errors[errors.length - 1]) < errorTol;
  // Select scheme based on derivative order
  let scheme;
  if (order == 1) scheme = centralDifferenceDfDx;
  else if (order == 2) scheme = centralDifferenceDfDx2;
  else throw `Order ${order} not supported`;

  let k = 1;
  // Seed estimate table with two initial estimates and extrapolation
  let estimateTable = [[scheme(f, x, h0), scheme(f, x, h0 / 2)]];
  estimateTable.push([richardsonExtrapolate(estimateTable[0][0], estimateTable[0][1], k)]);
  let errors = [richardsonErrorExtrapolate(estimateTable[0][0], estimateTable[0][1], k)];
  // Expand estimate table until error requirement is met or maxK is reached
  while (!checkErrorRequirement(errors)) {
    k += 1; // Increase order
    if (maxK && k > maxK) break;
    estimateTable[0].push(scheme(f, x, h0 / 2 ** k));
    for (let i = 1; i < k; i++) {
      const previousRowLastIdx = estimateTable[i - 1].length - 1;
      estimateTable[i].push(
        richardsonExtrapolate(estimateTable[i - 1][previousRowLastIdx - 1], estimateTable[i - 1][previousRowLastIdx], i)
      );
    }
    estimateTable.push([richardsonExtrapolate(estimateTable[k - 1][0], estimateTable[k - 1][1], k)]);
    errors.push(richardsonErrorExtrapolate(estimateTable[k - 1][0], estimateTable[k - 1][1], k));
  }

  if (!fullOutput) return estimateTable[k - 1][0];
  else {
    return {
      deriv: estimateTable[k - 1][0],
      estimatedError: errors[errors.length - 1],
      k: k,
      maxK: maxK,
      estimateTable,
      errors,
      success: k <= maxK && checkErrorRequirement(errors)
    };
  }
}
